import React, { useState, useEffect, useMemo, useRef } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css'
import {sdk} from './sdk.js';
import {sdkLocal} from './sdkLocal.js';
import './Loi25.css';
import { 
  FaShieldAlt, 
  FaTimes, 
  FaCog, 
  FaCheck, 
  FaEye,
  FaBullhorn,
  FaChartLine,
  FaCookieBite,
  FaInfoCircle
} from 'react-icons/fa';

const Loi25 = () => {
  // États pour la gestion du popup et des préférences
  const [isVisible, setIsVisible] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [preferences, setPreferences] = useState({
    essential: true, // Toujours activé, non modifiable
    analytics: false,
    marketing: false,
    personalization: false
  });
  
  // Vérifier si l'utilisateur a déjà fait un choix
  useEffect(() => {
    const consentGiven = localStorage.getItem('peupleqc-loi25-consent');
    const consentTimestamp = localStorage.getItem('peupleqc-loi25-timestamp');
    
    if (!consentGiven) {
      // Afficher le popup après un délai pour une meilleure UX
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 2000);
      
      return () => clearTimeout(timer);
    } else {
      // Charger les préférences existantes
      try {
        const savedPreferences = JSON.parse(consentGiven);
        setPreferences(prev => ({
          ...prev,
          ...savedPreferences
        }));
        
        // Vérifier si le consentement n'est pas trop ancien (1 an)
        const oneYearAgo = Date.now() - (365 * 24 * 60 * 60 * 1000);
        if (consentTimestamp && parseInt(consentTimestamp) < oneYearAgo) {
          setIsVisible(true);
        }
      } catch (error) {
        console.error('Erreur lors du chargement des préférences de confidentialité:', error);
        setIsVisible(true);
      }
    }
  }, []);
  
  // Sauvegarder les préférences
  const savePreferences = (newPreferences) => {
    localStorage.setItem('peupleqc-loi25-consent', JSON.stringify(newPreferences));
    localStorage.setItem('peupleqc-loi25-timestamp', Date.now().toString());
    setPreferences(newPreferences);
    
    // Émettre un événement personnalisé pour informer d'autres parties de l'application
    window.dispatchEvent(new CustomEvent('peupleqc-consent-updated', {
      detail: newPreferences
    }));
  };
  
  // Accepter tous les cookies
  const acceptAll = () => {
    const allAccepted = {
      essential: true,
      analytics: true,
      marketing: true,
      personalization: true
    };
    
    savePreferences(allAccepted);
    setIsVisible(false);
  };
  
  // Accepter seulement les essentiels
  const acceptEssential = () => {
    const essentialOnly = {
      essential: true,
      analytics: false,
      marketing: false,
      personalization: false
    };
    
    savePreferences(essentialOnly);
    setIsVisible(false);
  };
  
  // Fermer le popup (équivaut à accepter les essentiels)
  const closePopup = () => {
    acceptEssential();
  };
  
  // Ouvrir les préférences
  const openPreferences = () => {
    setShowPreferences(true);
  };
  
  // Fermer les préférences
  const closePreferences = () => {
    setShowPreferences(false);
  };
  
  // Sauvegarder les préférences personnalisées
  const saveCustomPreferences = () => {
    savePreferences(preferences);
    setShowPreferences(false);
    setIsVisible(false);
  };
  
  // Gérer le changement de préférence
  const handlePreferenceChange = (type) => {
    if (type === 'essential') return; // Les cookies essentiels ne peuvent pas être désactivés
    
    setPreferences(prev => ({
      ...prev,
      [type]: !prev[type]
    }));
  };
  
  // Ne pas afficher si pas visible
  if (!isVisible) {
    return null;
  }
  
  return (
    <>
      {/* Popup principal */}
      <div className={`loi25-popup ${isVisible ? 'loi25-visible' : ''}`}>
        <div className="loi25-container">
          <div className="loi25-content">
            <h3 className="loi25-title">
              <FaShieldAlt className="loi25-title-icon" />
              Protection de vos données personnelles
            </h3>
            <p className="loi25-text">
              Conformément à la Loi 25 du Québec, nous respectons votre vie privée. 
              Nous utilisons des cookies pour améliorer votre expérience sur Peuple Qc. 
              Vous pouvez personnaliser vos préférences ou consulter notre{' '}
              <a 
                href="/politique-confidentialite" 
                className="loi25-link"
                target="_blank" 
                rel="noopener noreferrer"
              >
                politique de confidentialité
              </a>.
            </p>
          </div>
          
          <div className="loi25-actions">
            <div className="loi25-btn loi25-btn-secondary" onClick={openPreferences}>
              <FaCog className="loi25-btn-icon" />
              Préférences
            </div>
            
            <div className="loi25-btn loi25-btn-secondary" onClick={acceptEssential}>
              Essentiels seulement
            </div>
            
            <div className="loi25-btn loi25-btn-primary" onClick={acceptAll}>
              <FaCheck className="loi25-btn-icon" />
              Tout accepter
            </div>
            
            <div className="loi25-btn loi25-btn-close" onClick={closePopup}>
              <FaTimes className="loi25-btn-icon" />
            </div>
          </div>
        </div>
      </div>
      
      {/* Modal des préférences */}
      {showPreferences && (
        <div className="loi25-modal-overlay" onClick={closePreferences}>
          <div className="loi25-modal" onClick={(e) => e.stopPropagation()}>
            <div className="loi25-modal-header">
              <h2 className="loi25-modal-title">
                <FaShieldAlt className="loi25-section-icon" />
                Préférences de confidentialité
              </h2>
              <div className="loi25-btn loi25-btn-close" onClick={closePreferences}>
                <FaTimes className="loi25-btn-icon" />
              </div>
            </div>
            
            <div className="loi25-modal-body">
              <div className="loi25-info-box">
                <p className="loi25-info-text">
                  <FaInfoCircle style={{ marginRight: '6px', color: '#1877f2' }} />
                  En accord avec la Loi 25 du Québec sur la protection des renseignements personnels, 
                  vous avez le contrôle total sur vos données. Vous pouvez modifier ces préférences à tout moment.
                </p>
              </div>
              
              <div className="loi25-section">
                <h3 className="loi25-section-title">
                  <FaCookieBite className="loi25-section-icon" />
                  Cookies essentiels
                </h3>
                <p className="loi25-section-text">
                  Ces cookies sont nécessaires au fonctionnement de base du site et ne peuvent pas être désactivés.
                </p>
                
                <div className="loi25-preference-item">
                  <div className="loi25-toggle-container">
                    <div className="loi25-toggle loi25-enabled">
                      <div className="loi25-toggle-handle"></div>
                    </div>
                  </div>
                  <div className="loi25-preference-content">
                    <h4 className="loi25-preference-title">Fonctionnalités de base</h4>
                    <p className="loi25-preference-desc">
                      Authentification, sécurité, préférences de langue et fonctionnalités essentielles de Peuple Qc.
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="loi25-section">
                <h3 className="loi25-section-title">
                  <FaChartLine className="loi25-section-icon" />
                  Cookies analytiques
                </h3>
                <p className="loi25-section-text">
                  Ces cookies nous aident à comprendre comment vous utilisez notre site pour l'améliorer.
                </p>
                
                <div className="loi25-preference-item">
                  <div 
                    className="loi25-toggle-container"
                    onClick={() => handlePreferenceChange('analytics')}
                  >
                    <div className={`loi25-toggle ${preferences.analytics ? 'loi25-enabled' : ''}`}>
                      <div className="loi25-toggle-handle"></div>
                    </div>
                  </div>
                  <div className="loi25-preference-content">
                    <h4 className="loi25-preference-title">Statistiques d'utilisation</h4>
                    <p className="loi25-preference-desc">
                      Mesure d'audience, pages visitées, temps passé sur le site (données anonymisées).
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="loi25-section">
                <h3 className="loi25-section-title">
                  <FaBullhorn className="loi25-section-icon" />
                  Cookies marketing
                </h3>
                <p className="loi25-section-text">
                  Ces cookies permettent de personnaliser les publicités selon vos intérêts.
                </p>
                
                <div className="loi25-preference-item">
                  <div 
                    className="loi25-toggle-container"
                    onClick={() => handlePreferenceChange('marketing')}
                  >
                    <div className={`loi25-toggle ${preferences.marketing ? 'loi25-enabled' : ''}`}>
                      <div className="loi25-toggle-handle"></div>
                    </div>
                  </div>
                  <div className="loi25-preference-content">
                    <h4 className="loi25-preference-title">Publicités ciblées</h4>
                    <p className="loi25-preference-desc">
                      Affichage de publicités pertinentes basées sur vos intérêts et votre activité.
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="loi25-section">
                <h3 className="loi25-section-title">
                  <FaEye className="loi25-section-icon" />
                  Cookies de personnalisation
                </h3>
                <p className="loi25-section-text">
                  Ces cookies améliorent votre expérience en retenant vos préférences.
                </p>
                
                <div className="loi25-preference-item">
                  <div 
                    className="loi25-toggle-container"
                    onClick={() => handlePreferenceChange('personalization')}
                  >
                    <div className={`loi25-toggle ${preferences.personalization ? 'loi25-enabled' : ''}`}>
                      <div className="loi25-toggle-handle"></div>
                    </div>
                  </div>
                  <div className="loi25-preference-content">
                    <h4 className="loi25-preference-title">Expérience personnalisée</h4>
                    <p className="loi25-preference-desc">
                      Recommandations de contenu, suggestions d'amis, personnalisation du fil d'actualité.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="loi25-modal-footer">
              <div className="loi25-btn loi25-btn-secondary" onClick={closePreferences}>
                Annuler
              </div>
              <div className="loi25-btn loi25-btn-primary" onClick={saveCustomPreferences}>
                <FaCheck className="loi25-btn-icon" />
                Enregistrer mes préférences
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export { Loi25 };